import Chip from "@mui/material/Chip";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import { MRTNoMemo } from "_shared/_views/tables/table";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import HolderCell from "permissions/_views/grant-row/holder-cell";
import { type PersonNames, personNameState } from "permissions/_views/targets/person-name-states";
import { KIND_HOLDERS_PAGE_SIZE, type KindHolder, type KindHolders } from "permissions/_views/kinds/use-kind-holders";
import { holderRecordCount } from "permissions/_views/wording/permission-wording";
import type { MRT_ColumnDef, MRT_PaginationState, MRT_TableOptions } from "material-react-table";

/**
 * A per-record kind's holders on the kind page, in the shared table: each person with how many
 * records they hold, a page of people at a time (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`,
 * "Layout by width"). The records themselves are on each person's page, so a row is the person and a
 * count, and the table brings the paging and the empty and failed states
 * (`docs/software-design/table-empty-states.md`).
 */

/** What the table lists, as its empty states say it: "No holders of one document". */
export function holdersSubject(kind: Pick<PermissionKind, "name">): string {
  return `holders of ${kind.name}`;
}

export type KindRecordHoldersTableProps = Readonly<{
  kind: PermissionKind;
  holders: KindHolders;
  pageIndex: number;
  onPageChange: (pageIndex: number) => void;
  /** Each holder's name, or why the directory gave none. */
  names: PersonNames;
}>;

export default function KindRecordHoldersTable(props: KindRecordHoldersTableProps) {
  const { kind, holders, pageIndex, onPageChange, names } = props;
  const noun = kind.targetNoun ?? "record";
  const pagination: MRT_PaginationState = { pageIndex, pageSize: KIND_HOLDERS_PAGE_SIZE };

  const columns: MRT_ColumnDef<KindHolder>[] = [
    {
      id: "person",
      header: "Person",
      Cell: ({ row }) => (
        <HolderCell
          userId={row.original.granteeUserId}
          individual={names.individuals.get(row.original.granteeUserId.value.toLowerCase())}
          state={personNameState(names, row.original.granteeUserId.value)}
          onRetry={names.retry}
        />
      ),
    },
    {
      id: "records",
      header: `${noun.charAt(0).toUpperCase()}${noun.slice(1)}s`,
      grow: false,
      muiTableHeadCellProps: { align: "right" },
      muiTableBodyCellProps: { align: "right" },
      Cell: ({ row }) => <Chip size="small" variant="outlined" label={holderRecordCount(noun, row.original.count)} />,
    },
  ];

  const options: MRT_TableOptions<KindHolder> = {
    columns,
    data: [...holders.holders],
    // While loading, MRT draws skeleton rows from placeholder objects that carry no person, and asks
    // each one for its id all the same.
    getRowId: (holder, index) => holder.granteeUserId?.value ?? `loading-${index}`,
    layoutMode: "grid",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableSorting: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    manualPagination: true,
    rowCount: holders.total ?? 0,
    onPaginationChange: (updater) => {
      const next = typeof updater === "function" ? updater(pagination) : updater;
      if (next.pageIndex !== pageIndex) onPageChange(next.pageIndex);
    },
    muiPaginationProps: { rowsPerPageOptions: [KIND_HOLDERS_PAGE_SIZE], showRowsPerPage: false },
    muiTablePaperProps: { variant: "outlined", elevation: 0 },
    muiTableProps: { "aria-label": `Holders of ${kind.name}` },
    renderEmptyRowsFallback: emptyRowsFallback({
      subject: holdersSubject(kind),
      isLoading: holders.isFetching,
      isError: Boolean(holders.error),
      errorDescription: holders.error,
      onRetry: holders.retry,
    }),
    state: {
      pagination,
      isLoading: holders.isLoading,
      showProgressBars: holders.isFetching,
    },
  };

  return <MRTNoMemo<KindHolder> options={options} />;
}
